import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Building2, User, Mail, Lock, ArrowRight, CheckCircle } from 'lucide-react';

export default function Signup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    organization_name: '',
    name: '',
    email: '',
    password: '',
    confirm_password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/signup`, {
        organization_name: formData.organization_name,
        name: formData.name,
        email: formData.email,
        password: formData.password,
      });
      
      toast.success('Account created! Please sign in.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Unlimited free trial - no credit card required',
    'Full access to all features',
    'Import your existing clients easily',
    'Cancel anytime, no questions asked'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-[#E6F7F7]/30 flex">
      {/* Left Panel - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#00A3A1] to-[#008B89] p-12 items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#E94E89] rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-lg">
          <img 
            src="/simplly_logo.png"
            alt="Simplly"
            className="h-16 object-contain brightness-0 invert mb-8"
          />
          <h1 className="text-4xl font-bold text-white mb-6">
            Start managing clients the smart way
          </h1>
          <p className="text-xl text-white/80 mb-8">
            Join hundreds of consultancies who've simplified their client management with Simplly.
          </p>
          <ul className="space-y-4">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-center gap-3 text-white/90">
                <CheckCircle className="h-5 w-5 text-[#E94E89] flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <Card className="w-full max-w-md shadow-xl border-slate-200/50 bg-white/80 backdrop-blur-sm">
          <div className="h-1 bg-gradient-to-r from-[#00A3A1] to-[#E94E89]" />
          
          <CardHeader className="space-y-2 text-center pt-8 pb-4">
            <div className="lg:hidden mb-4">
              <img 
                src="/simplly_logo.png"
                alt="Simplly"
                className="h-14 mx-auto object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Create your account</CardTitle>
            <CardDescription className="text-slate-500">
              Get started with your free trial
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Organization Name */}
              <div className="space-y-2">
                <Label htmlFor="organization_name" className="text-sm font-medium text-slate-700">
                  Organization Name
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="organization_name"
                    data-testid="signup-org-input"
                    placeholder="Your Company Name"
                    value={formData.organization_name}
                    onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                  />
                </div>
              </div>

              {/* Your Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                  Your Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="name"
                    data-testid="signup-name-input"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Work Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="signup-email-input"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="signup-password-input"
                    type="password"
                    placeholder="At least 8 characters"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-sm font-medium text-slate-700">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirm_password"
                    data-testid="signup-confirm-password-input"
                    type="password"
                    placeholder="Repeat your password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    className="pl-10 h-11 border-slate-200 focus:border-[#00A3A1] focus:ring-[#00A3A1]/20"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="signup-submit-button"
                className="w-full h-11 bg-gradient-to-r from-[#00A3A1] to-[#008B89] hover:from-[#008B89] hover:to-[#007A78] text-white font-medium shadow-lg shadow-[#00A3A1]/20 transition-all duration-200 group"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating account...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Create Account
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                )}
              </Button>

              <p className="text-center text-xs text-slate-500">
                By signing up, you agree to our{' '}
                <a href="#" className="text-[#00A3A1] hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-[#00A3A1] hover:underline">Privacy Policy</a>
              </p>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <p className="text-sm text-slate-600">
                Already have an account?{' '}
                <Link to="/login" className="text-[#00A3A1] font-medium hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
